import { fabric } from "fabric";
import { debounce } from "lodash";
import { InteractionMode } from "board-core/plugin/InteractionModeManager";

import { useBoardToolbarStore } from "../../helper/store/toolbar";
import { DragCopyState, FabricListeners } from "./types";
import { CALC_OFFSET_DEBOUNCE_MS, CALC_OFFSET_VALIDITY_MS } from "./config";
import { getZoomShortcutHint, getCurrentPlatform } from "./utils/platform";
import { isImageOrVideoObject } from "../../helper/layerUtils";

import {
  createTargetFindLockState,
  lockTargetFind,
  unlockTargetFind,
  forceResetTargetFind
} from "./modules/targetLock";

import {
  createMiddleDragState,
  handleMiddleMouseDown,
  handleMiddleMouseUp,
  handleMiddleDragMove
} from "./modules/middleDrag";

import {
  handleWheelZoom,
  handleWheelPan
} from "./modules/zoom";

import { createCursorTracker } from "./modules/cursor";

import {
  createDragCopyContext,
  handleDragCopyMoving,
  completeDragCopy,
  removeGhosts
} from "./modules/dragCopy";

/**
 * =====================================================
 * 鼠标事件模块 - 主入口
 * =====================================================
 * 功能模块:
 * 1. 中键拖拽平移画布
 * 2. 滚轮缩放 (Mac: Cmd+滚轮, Windows: Ctrl+滚轮)
 * 3. 触摸板/滚轮平移
 * 4. Alt + 拖拽复制对象
 *
 * 目录结构:
 * mouseEvents/
 * ├── types.ts           # 类型定义
 * ├── config.ts          # 配置常量
 * ├── utils/
 * │   └── platform.ts    # 平台检测工具
 * ├── modules/
 * │   ├── targetLock.ts  # 目标查找锁定
 * │   ├── middleDrag.ts  # 中键拖拽平移
 * │   ├── zoom.ts        # 滚轮缩放
 * │   └── dragCopy.ts    # Alt+拖拽复制
 * └── index.ts           # 主入口 (本文件)
 */

/**
 * 绑定鼠标交互事件
 * @param canvas Fabric 画布实例
 * @returns 包含 Fabric 事件绑定和 Window 事件解绑函数的对象
 */
export const useMouseEvents = (canvas: fabric.Canvas) => {
  const boardToolbarStore = useBoardToolbarStore();

  // 防抖版的 calcOffset，用于中键拖拽时避免频繁计算
  const debouncedCalcOffset = debounce(() => {
    canvas.calcOffset();
  }, CALC_OFFSET_DEBOUNCE_MS);

  let viewportInteractingResetTimer: number | null = null;

  // 初始化各模块状态
  const middleDragState = createMiddleDragState();
  const dragCopyContext = createDragCopyContext();
  const targetFindLockState = createTargetFindLockState();

  // 初始化光标追踪器
  const cursorTracker = createCursorTracker(canvas);

  // 标记 canvas mouse:up 是否已经处理
  let canvasMouseUpHandled = false;

  // 包装锁定/解锁函数，传入 canvas 和 state
  const doLockTargetFind = () => lockTargetFind(canvas, targetFindLockState);
  const doUnlockTargetFind = () => unlockTargetFind(canvas, targetFindLockState);
  const doForceResetTargetFind = () => forceResetTargetFind(canvas, targetFindLockState);
  const doRemoveGhosts = () => removeGhosts(canvas, dragCopyContext);

  // ==========================================
  // 事件处理器
  // ==========================================

  /**
   * 全局鼠标松开处理 - 用于兜底处理异常情况
   * 处理场景：
   * 1. 中键松开 - 结束平移
   * 2. 左键在画布外松开 - 清理拖拽复制状态
   */
  const handleGlobalMouseUp = (e: MouseEvent): void => {
    // 中键松开 - 结束平移
    if (e.button === 1 && middleDragState.isDragging) {
      handleMiddleMouseUp(canvas, middleDragState, boardToolbarStore, doUnlockTargetFind);
    }

    // 左键松开 - 兜底处理
    if (e.button === 0) {
      // 尝试解锁 TargetFind
      doUnlockTargetFind();

      // 强力兜底：检测并修复死锁状态
      doForceResetTargetFind();

      // 确保工具栏状态恢复
      boardToolbarStore.setObjectDragging(false);

      // 延迟检查 canvas mouse:up 是否已处理
      setTimeout(() => {
        if (canvasMouseUpHandled) {
          canvasMouseUpHandled = false;
          return;
        }

        // 如果 isDragging 仍为 true，说明 canvas 的 mouse:up 真的没触发
        if (dragCopyContext.isDragging) {
          // 恢复透明度
          const activeObject = canvas.getActiveObject();
          if (activeObject) {
            activeObject.set("opacity", 1);
          }

          // 重置状态
          dragCopyContext.isDragging = false;
          dragCopyContext.isCopyMode = false;
          dragCopyContext.initialObjectStates = [];

          // 清理 Ghost
          doRemoveGhosts();

          canvas.requestRenderAll();
        }
      }, 0);
    }
  };

  /**
   * 鼠标按下事件处理
   * 根据按键类型分发到不同处理器
   */
  const handleMouseDown = (opt: fabric.IEvent<MouseEvent>): void => {
    const e = opt.e;

    // 定期更新坐标偏移缓存
    const now = performance.now();
    const last = (canvas as any).__lastCalcOffsetTs || 0;
    if (now - last > CALC_OFFSET_VALIDITY_MS) {
      canvas.calcOffset();
      (canvas as any).__lastCalcOffsetTs = now;
    }

    // 右键点击直接返回
    if (e.button === 3) return;

    // 中键按下 - 开始平移
    if (e.button === 1) {
      handleMiddleMouseDown(
        canvas,
        middleDragState,
        boardToolbarStore,
        doLockTargetFind,
        e
      );
      return;
    }

    // 左键按下 - 准备拖拽复制
    if (e.button === 0) {
      handleLeftMouseDown(e, opt.target);
    }
  };

  /**
   * 左键按下处理 - 初始化拖拽复制状态
   */
  const handleLeftMouseDown = (e: MouseEvent, target?: fabric.Object): void => {
    try {
      // 绿光扫描期间仅锁定被扫对象（SplitScanOverlay per-object 锁，target 不会命中它），
      // 其余对象交互与拖拽复制照常，不再全局早退
      const anyCanvas = canvas as any;
      const isDragMode =
        boardToolbarStore.isTemporaryDrag ||
        boardToolbarStore.activeTool === InteractionMode.DRAG ||
        boardToolbarStore.currentToolMode === "drag";

      // 局部修改模式保护：Inpaint 面板打开时，画布由 MaskRenderer 接管 pointer events，
      // 此处不应重置 skipTargetFind、恢复 canvas.selection 或设置拖拽目标，
      // 否则会覆盖 inpaint 对目标对象的锁定（selectable/evented/lockMovement），
      // 导致 Fabric 将涂抹事件解释为"选中/拖拽对象"而非"绘制笔触"
      const isInpaintMode = boardToolbarStore.isInpaintPanelVisible;

      // 重置 Fabric 内部状态（局部修改模式下跳过，避免破坏 MaskRenderer 的 targetFind 控制）
      if (anyCanvas.skipTargetFind && !isInpaintMode) {
        anyCanvas.skipTargetFind = false;
        targetFindLockState.originalValue = null;
        targetFindLockState.lockCount = 0;
      }

      // 拖拽模式下保持禁选中，避免长按/拖动触发元素选中
      if (isDragMode) {
        canvas.selection = false;
        return;
      }

      if (canvas.selection === false && !isInpaintMode) {
        canvas.selection = true;
      }

      // 局部修改模式下不设置拖拽目标，直接返回
      if (isInpaintMode) return;

      // 多选修饰键按下时不进入拖拽复制（Ctrl: Windows/Linux, Cmd: Mac, Shift: 通用）
      if (e.ctrlKey || e.shiftKey || e.metaKey) return;

      // 重置拖拽复制状态
      dragCopyContext.isDragging = true;
      dragCopyContext.isCopyMode = false;
      dragCopyContext.initialObjectStates = [];
      dragCopyContext.activeSelectionState = null;
      doRemoveGhosts();

      // 修复：如果 target 为 undefined 但存在 ActiveSelection，使用当前选中的对象
      let actualTarget = target;
      if (!actualTarget) {
        const activeObject = canvas.getActiveObject();
        if (activeObject && activeObject.type === "activeSelection") {
          actualTarget = activeObject;
        }
      }

      if (actualTarget && (actualTarget as any).id !== "workspace") {
        setupDragTarget(actualTarget);
      }
    } catch (error) {
      console.error("Mouse down handler error:", error);
    }
  };

  /**
   * 设置拖拽目标的可交互状态
   * 确保目标对象可以正常响应拖拽操作
   */
  const setupDragTarget = (target: fabric.Object): void => {
    const normalizeInteractiveState = (obj: fabric.Object) => {
      if ((obj as any).lock) return;
      obj.set({
        evented: true,
        selectable: true,
        hoverCursor: "move",
        moveCursor: "move"
      });
      obj.setCoords();
    };

    if (target.type === "activeSelection") {
      // 确保 ActiveSelection 本身也是可交互的
      target.set({
        lockMovementX: false,
        lockMovementY: false,
        hasControls: true,
        evented: true,
        selectable: true,
        perPixelTargetFind: false,
        hoverCursor: "move",
        moveCursor: "move"
      });

      (target as fabric.ActiveSelection).getObjects().forEach((item) => {
        normalizeInteractiveState(item);
      });
    } else {
      normalizeInteractiveState(target);
    }

    if (target.type === "activeSelection") {
      // 记录 ActiveSelection 的初始状态
      dragCopyContext.activeSelectionState = target.toObject();
      delete dragCopyContext.activeSelectionState.objects;
    }

    const targets =
      target.type === "activeSelection"
        ? (target as fabric.ActiveSelection).getObjects()
        : [target];

    // 记录初始状态
    dragCopyContext.initialObjectStates = targets.map((t): DragCopyState => {
      // 安全获取图片元素
      let imgElement: HTMLImageElement | null = null;
      try {
        if (isImageOrVideoObject(t) && typeof (t as fabric.Image).getElement === "function") {
          const element = (t as fabric.Image).getElement();
          if (element instanceof HTMLImageElement) {
            imgElement = element;
          }
        }
      } catch (e) {
        console.warn("Failed to get image element", e);
      }

      return {
        id: (t as any).id,
        left: t.left || 0,
        top: t.top || 0,
        object: t,
        index: canvas.getObjects().indexOf(t),
        imageElement: imgElement,
        matrix: t.calcTransformMatrix(),
        centerPoint: t.group
          ? fabric.util.transformPoint(t.getCenterPoint(), t.group.calcTransformMatrix())
          : t.getCenterPoint()
      };
    });
  };

  /**
   * 鼠标移动事件处理
   * 同时处理中键拖拽平移和其他移动逻辑
   */
  const handleMouseMove = (opt: fabric.IEvent<MouseEvent>): void => {
    // 追踪光标位置
    cursorTracker.move(opt);

    const e = opt.e;
    if (!e) return;

    // 中键拖拽平移
    handleMiddleDragMove(canvas, middleDragState, debouncedCalcOffset, e);
  };

  /**
   * 对象移动事件处理 - Alt + 拖拽复制
   */
  const handleObjectMoving = (opt: fabric.IEvent<MouseEvent>): void => {
    handleDragCopyMoving(canvas, dragCopyContext, boardToolbarStore, opt.e);
  };

  /**
   * 鼠标松开事件处理
   * 处理中键结束和拖拽复制完成
   */
  const handleMouseUp = (opt: fabric.IEvent<MouseEvent>): void => {
    canvasMouseUpHandled = true;
    boardToolbarStore.setObjectDragging(false);
    doUnlockTargetFind();

    // 中键松开
    if (opt.e.button === 1) {
      handleMiddleMouseUp(canvas, middleDragState, boardToolbarStore, doUnlockTargetFind);
    }

    // Alt + 拖拽复制完成
    if (
      dragCopyContext.isDragging &&
      dragCopyContext.isCopyMode &&
      dragCopyContext.initialObjectStates.length > 0
    ) {
      completeDragCopyWithCleanup();
    } else {
      // 普通释放，重置状态
      resetDragCopyState();
    }
  };

  /**
   * 完成拖拽复制并清理状态
   */
  const completeDragCopyWithCleanup = (): void => {
    // 立即恢复被拖拽对象的透明度
    const activeObject = canvas.getActiveObject();
    if (activeObject) {
      activeObject.set("opacity", 1);
    }

    const currentInitialStates = [...dragCopyContext.initialObjectStates];

    // 立即重置全局状态，防止重复触发
    dragCopyContext.isDragging = false;
    dragCopyContext.isCopyMode = false;
    dragCopyContext.initialObjectStates = [];
    dragCopyContext.ghostObjects = [];

    // 使用 requestAnimationFrame 确保在下一帧处理逻辑
    requestAnimationFrame(() => {
      completeDragCopy(canvas, dragCopyContext, currentInitialStates);
    });
  };

  /**
   * 重置拖拽复制状态
   */
  const resetDragCopyState = (): void => {
    dragCopyContext.isDragging = false;
    dragCopyContext.isCopyMode = false;
    dragCopyContext.initialObjectStates = [];
    doRemoveGhosts();
  };

  /**
   * 鼠标滚轮事件处理
   * 优先处理缩放，未触发则处理平移
   */
  const handleMouseWheel = (opt: fabric.IEvent<WheelEvent>): void => {
    const evt = opt.e;

    boardToolbarStore.setViewportInteracting(true);
    if (viewportInteractingResetTimer) {
      window.clearTimeout(viewportInteractingResetTimer);
    }
    viewportInteractingResetTimer = window.setTimeout(() => {
      viewportInteractingResetTimer = null;
      boardToolbarStore.setViewportInteracting(false);
    }, 120);

    // 尝试处理缩放
    const zoomHandled = handleWheelZoom(canvas, evt);

    // 未触发缩放，则处理平移
    if (!zoomHandled) {
      handleWheelPan(canvas, evt);
    }

    evt.preventDefault();
    evt.stopPropagation();
  };

  /**
   * 阻止浏览器默认的缩放行为
   * 防止 Ctrl + 滚轮触发浏览器页面缩放
   */
  const preventBrowserZoom = (e: WheelEvent): void => {
    const target = e.target as HTMLElement;

    // 如果在画布区域内，阻止默认行为
    if (target.closest(".canvas-workspace")) {
      // 排除内部可滚动的元素
      if (target.closest(".message-list, textarea, .scrollable-container")) {
        return;
      }
      e.preventDefault();
    }

    // 阻止 Ctrl + 滚轮的浏览器缩放
    if (e.ctrlKey) {
      e.preventDefault();
    }
  };

  // ==========================================
  // 事件绑定
  // ==========================================

  /**
   * 绑定 Fabric.js 事件
   * @returns 事件监听器映射，用于后续解绑
   */
  const bindFabricEvents = (): FabricListeners => {
    const listeners: FabricListeners = {
      "mouse:move": handleMouseMove,
      "mouse:wheel": handleMouseWheel,
      "mouse:down": handleMouseDown,
      "object:moving": handleObjectMoving,
      "mouse:up": handleMouseUp,
      "mouse:out": cursorTracker.out
    };

    return listeners;
  };

  /**
   * 点击前强制刷新坐标：滚轮滚动后 60ms 防抖内点击会导致检测不准，
   * 在 capture 阶段先 flush，确保 Fabric findTarget 时坐标已更新
   */
  const handleCaptureMouseDown = (): void => {
    canvas.fire("viewport:flushSync");
  };

  /**
   * 绑定 Window 级别事件
   * @returns 解绑函数，组件卸载时调用
   */
  const bindWindowEvents = (): (() => void) => {
    document.addEventListener("mouseup", handleGlobalMouseUp);
    window.addEventListener("wheel", preventBrowserZoom, { passive: false });

    const upperCanvas = (canvas as any).upperCanvasEl as HTMLElement | undefined;
    if (upperCanvas) {
      upperCanvas.addEventListener("mousedown", handleCaptureMouseDown, { capture: true });
    }

    return () => {
      document.removeEventListener("mouseup", handleGlobalMouseUp);
      window.removeEventListener("wheel", preventBrowserZoom);
      if (upperCanvas) {
        upperCanvas.removeEventListener("mousedown", handleCaptureMouseDown, { capture: true });
      }
      if (viewportInteractingResetTimer) {
        window.clearTimeout(viewportInteractingResetTimer);
        viewportInteractingResetTimer = null;
      }
      boardToolbarStore.setViewportInteracting(false);
    };
  };

  return {
    bindFabricEvents,
    bindWindowEvents
  };
};

// ============================================
// 导出配置和工具 (供外部使用)
// ============================================

export { getZoomShortcutHint, getCurrentPlatform };
